<script setup>
// core
import {ref, computed, onMounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useStore} from 'vuex'

// components
import ActivationFirstStep from "@/components/Tiktok/ActivationFirstStep.vue";
import ActivationResult from "@/components/Tiktok/ActivationResult.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";

const route = useRoute()
const router = useRouter()
const store = useStore()

// the tiktok callback sends the user back here with the outcome in the query:
// ?activation=success&user_id=&username= or ?activation=failed&reason=
const isSuccess = computed(() => route.query.activation === 'success')
const isFailed = computed(() => route.query.activation === 'failed')

// the query is public: a success screen is only shown once the account is
// confirmed to belong to the account currently logged in
const isVerifying = ref(false)
const verifiedUsername = ref('')

const showResult = computed(() => isFailed.value || !!verifiedUsername.value)

const clearResult = () => router.replace({name: 'TiktokActivation', query: {}})

const verifySuccess = async () => {
    const userId = route.query.user_id

    if (!userId) return clearResult()

    isVerifying.value = true

    try {
        const account = await store.dispatch('tiktok/fetchAccount', {userId})
        // the name from the response, not from the query
        verifiedUsername.value = account?.username || userId
    } catch (e) {
        // someone else's account, a stale link or a failed request: no result
        // screen at all, just the usual page
        clearResult()
    } finally {
        isVerifying.value = false
    }
}

onMounted(() => {
    if (isSuccess.value) verifySuccess()
})
</script>

<template>
    <section class="activation">
        <InfoDataTextRecurring v-if="isVerifying" value="loading" height="240px"/>

        <TransitionGroup v-else name="fade" mode="out-in">
            <ActivationResult
                v-if="showResult"
                key="result"
                :status="route.query.activation"
                :username="verifiedUsername"
                :reason="route.query.reason"
                @retry="clearResult"
            />
            <ActivationFirstStep v-else key="first"/>
        </TransitionGroup>
    </section>
</template>

<style scoped lang="scss">
.activation {
    padding: 0 20px;

    // the same heading weight and spacing the messenger pages use
    :deep(.txt) {
        font-weight: 500;
        margin-bottom: 30px;
    }
}
</style>
